import {t} from "@lingui/core/macro";
import * as React from "react";

import {
    useOnMountEffect,
    type StyleType,
} from "@khanacademy/wonder-blocks-core";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    markMinorConversions,
    usePublishAnalyticsEvent,
    type MinorConversionData,
} from "@khan/analytics-learning-time";
import {
    calculateNextUrl,
    createReauthUrl,
    LoginButton,
    reauthWithThirdParty,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {CookieCategory, getConsentCookieCategories} from "@khan/data-cookies";
import Log from "@khan/logging";
import {preserveMarketingParams, redirect, useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {getErrorMessageByCode, loginWithFacebook} from "./facebook-graphql.ts";
import {FacebookIcon} from "./facebook-icon.tsx";
import {getFacebookAuthToken, loadFacebookApi} from "./facebook-util.ts";

interface CompleteLoginOverrideParamsFacebook {
    userData: LoginTypeData;
}

interface Props {
    disabled?: boolean;
    continueUrl?: string;
    role?: Role;
    size?: "small" | "medium" | "large";
    style?: StyleType;
    minorConversions?: ReadonlyArray<MinorConversionData>;
    classCodes?: Array<string>;
    // An invite ID that a user is signing up with
    inviteId?: string;
    // Callback for when doLogin succeeds, overrides the completeLogin behavior
    //
    // If this is not provided, completeLogin will be executed as normal
    //
    // If this is provided, the passed function will be executed instead of completeLogin
    //
    // If your override function requires additional parameters, add them to the CompleteLoginOverrideParams type
    completeLoginOverride?: (
        options: CompleteLoginOverrideParamsFacebook,
    ) => Promise<void>;
    // The handler to be executed when reauthentication is successful (to be set
    // only if the button is used for reauth)
    onReauthSuccess?: (url: string) => unknown;
    /**
     * Callback for when the login fails.
     *
     * This can be caused due to Facebook auth errors or to errors caused in the
     * loginWithFacebook mutation.
     */
    onError?: (message: string) => unknown;
    /**
     * Callback for when the reauth flow fails.
     *
     * This is executed when the reauthentication with Facebook fails.
     */
    onReauthError?: (url: string) => unknown;
    /**
     * Event Type for the CEDAR event which will be published on click
     *
     * We only pass the event type and rely on this component to determine
     * the rest of the event data.
     *
     * NOTE: If you update the list of possible values for this type, make sure
     * to update `handleClick` to publish the correct event.
     */
    cedarEventType?: "SignupFacebookButtonClicked";
    /**
     * If true, show only the icon, no text.
     */
    iconOnly?: boolean;
    /**
     * Whether form validation is required before executing the login logic
     */
    validationRequired?: boolean;
    /**
     * Whether the current form is valid. Determined by the TOS checkbox
     */
    isFormValid?: boolean;
    /**
     * Called when the form is invalid and validation is required.
     */
    onValidationError?: () => void;
}

/**
 * Handles the UI part of the Facebook login integration.
 *
 * For more information about how the login flow works:
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/658276814/Sign+in+with+Facebook
 */
export function FacebookButton(props: Props): React.ReactNode {
    const location = useLocation();
    const {gqlClient} = useApolloGqlClient();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const [error, setError] = React.useState<Error | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [loggingIn, setLoggingIn] = React.useState<boolean>(false);
    const [functionalCookiesEnabled, setFunctionalCookiesEnabled] =
        React.useState<boolean>(false);
    const [facebookApiLoaded] = React.useState<boolean>(false);

    useOnMountEffect(() => {
        const categories = getConsentCookieCategories();

        const funcCookiesEnabled =
            categories.get(CookieCategory.Functional) ?? false;
        setFunctionalCookiesEnabled(funcCookiesEnabled);

        if (funcCookiesEnabled) {
            preloadFacebookApi();
        }

        window.addEventListener(
            "OneTrustGroupsUpdated",
            handleOneTrustGroupsUpdated,
        );

        // On Unmount
        return () => {
            window.removeEventListener(
                "OneTrustGroupsUpdated",
                handleOneTrustGroupsUpdated,
            );
        };
    });

    const handleOneTrustGroupsUpdated = (event: any) => {
        const categories = getConsentCookieCategories();

        setFunctionalCookiesEnabled(
            categories.get(CookieCategory.Functional) ?? false,
        );
        preloadFacebookApi();
    };

    /**
     * Preload Facebook JS SDK. This help us avoiding any issues when the user
     * tries to open the FB Dialog in mobile browsers.
     *
     * @see https://developers.facebook.com/docs/reference/javascript/FB.login
     * Section: Avoid Popup Blocking
     */
    const preloadFacebookApi = () => {
        if (facebookApiLoaded) {
            return;
        }

        setLoading(true);

        // If Functional cookies aren't enabled, load the script as plain text.
        const categories = getConsentCookieCategories();
        if (!categories.get(CookieCategory.Functional)) {
            setError(
                new Error(
                    "Login with Facebook requires Functional cookies to be enabled",
                ),
            );
            return;
        }

        loadFacebookApi()
            .then((FB) => {
                // NOTE(kevinb): We reset the value of `error` here to handle
                // the case where the user has disabled Functional cookies and
                // then renabled them later.
                setLoading(false);
                setError(null);
                // TODO(davemcalpine): we should add this line of code. But
                // setting the var to true is not how it previously worked.
                // Since I'm currently just converting this component to a
                // function component, I'm going to wait to add it.
                // setFacebookApiLoaded(true);
                return FB;
            })
            .catch((error) => {
                // We set the error state but do not clear the loading state.
                // This is so that we can still show the button as disabled
                // since clicking the button won't do anything if the FB API
                // hasn't been loaded.
                setLoading(false);
                setError(error);
            });
    };

    /**
     * Logs in case we get any error from the FB SDK or from the GraphQL
     * mutation.
     * @param {Error} error The error thrown in the current context.
     */
    const handleError = (error: Error) => {
        const {onError} = props;

        const errorMessage = getErrorMessageByCode(error.message);

        Log.log("Something unexpected happened!", {
            code: error.message,
            message: errorMessage,
        });

        if (onError) {
            onError(errorMessage);
        }

        setError(null);
        setLoading(false);
        setLoggingIn(false);
    };

    const handleClick = (e: React.SyntheticEvent) => {
        if (props.validationRequired && !props.isFormValid) {
            // Trigger error callback for invalid form when validation is required
            props.onValidationError?.();
            return;
        }
        // Allow to using Facebook auth only when the JS SDK is loaded or we
        // don't get any error.
        if (loading || error) {
            return;
        }

        setLoading(true);

        const {minorConversions} = props;

        if (props.cedarEventType === "SignupFacebookButtonClicked") {
            publishAnalyticsEvent(
                {
                    eventType: "SignupFacebookButtonClicked",
                    eventSchemaVersion: 1,
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
        }

        if (minorConversions) {
            markMinorConversions(minorConversions);
        }

        getFacebookAuthToken()
            .then((token) => {
                // Notify the user via UI that it's being logged in.
                setLoggingIn(true);

                const reauth = !!props.onReauthSuccess;

                reauth ? doReauth(token) : doLogin(token);
            })
            .catch((error) => {
                // signIn error
                if (error) {
                    handleError(error);
                }

                const {onReauthError} = props;

                if (onReauthError) {
                    // get redirect URL that will display the error message.
                    const reauthUrlError = createReauthUrl({
                        loginType: "facebook",
                        error: error.message,
                    });
                    onReauthError(reauthUrlError);
                }
            });
    };

    const completeLogin = async (userData: LoginTypeData): Promise<void> => {
        const continueUrl = props.continueUrl || "/";

        const calculatedNextUrl = await calculateNextUrl({
            gqlClient: gqlClient,
            location: location,
            loginType: "facebook",
            continueUrl,
            isFirstLogin: userData.isFirstLogin,
            joinClassrooms: userData.joinClassrooms,
        });

        redirect(
            preserveMarketingParams(
                calculatedNextUrl,
                new URLSearchParams(location.search),
            ),
        );
    };

    const doLogin = (token: string): void => {
        loginWithFacebook(gqlClient, {
            token: token,
            signupCodes: props.classCodes,
            role: props.role,
            inviteId: props.inviteId,
        })
            .then((userData) => {
                if (props.completeLoginOverride) {
                    props.completeLoginOverride({userData});
                } else {
                    completeLogin(userData);
                }
            })
            .catch((error) => {
                handleError(error);
            });
    };

    const doReauth = (token: string): void => {
        reauthWithThirdParty(gqlClient, "facebook", token)
            .then((response) => {
                // get redirect URL that will display the success message.
                const reauthUrl = createReauthUrl({loginType: "facebook"});

                if (props.onReauthSuccess) {
                    props.onReauthSuccess(reauthUrl);
                }

                setLoggingIn(false);
                setLoading(false);
            })
            .catch((error) => {
                handleError(error);
                // get redirect URL that will display the error message.
                const reauthUrlError = createReauthUrl({
                    loginType: "facebook",
                    error: error.message,
                });

                if (props.onReauthError) {
                    props.onReauthError(reauthUrlError);
                } else {
                    redirect(reauthUrlError);
                }

                setLoading(false);
            });
    };

    const disabled = loading || !!props.disabled || !functionalCookiesEnabled;

    const loginButton = (
        <LoginButton
            title={
                !loggingIn ? t`Continue with Facebook` : t`Logging you in...`
            }
            iconComponent={FacebookIcon}
            disabled={disabled}
            onClick={handleClick}
            size={props.size}
            style={props.style}
            iconOnly={props.iconOnly}
        />
    );

    // If functional cookies are disabled, we show a
    // tooltip explaining that the user needs to enable functional cookies.
    return !functionalCookiesEnabled ? (
        <Tooltip
            content={t`Enable Functional Cookies to log in with Facebook`}
            testId="facebook-button-tooltip"
        >
            {loginButton}
        </Tooltip>
    ) : (
        loginButton
    );
}
